import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRequestStore } from '../../store/requestStore';
import { SURVEY_SERVICE_TYPES } from '../../types';
import { MapPicker } from '../../components/MapPicker';
import { useToast } from '../../components/ToastProvider';
import { useTheme } from '../../theme';
import { AnimatedIcon } from '../../components/icons';

export const CreateRequestScreen: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedType = searchParams.get('serviceType') || '';
  const { createRequest, isLoading } = useRequestStore();
  const { showToast } = useToast();
  const { isDark } = useTheme();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    service_type: preselectedType,
    description: '',
    address: '',
    latitude: null as number | null,
    longitude: null as number | null,
    deadline: '',
    preferred_date: '',
  });
  const [error, setError] = useState('');
  const [gpsLoading, setGpsLoading] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  const set = (key: string, val: any) => setForm((f) => ({ ...f, [key]: val }));

  const validateStep1 = () => {
    if (!form.service_type) return 'Please select a service type.';
    if (form.description.trim().length < 10) return 'Description must be at least 10 characters.';
    if (!form.address.trim()) return 'Property address is required.';
    return null;
  };

  const validateStep2 = () => {
    if (form.latitude == null || form.longitude == null) return 'Please drop a pin on the map or use GPS.';
    return null;
  };

  const handleNext = () => {
    setError('');
    if (step === 1) {
      const err = validateStep1();
      if (err) { setError(err); showToast(err, 'error'); return; }
      setStep(2);
    } else if (step === 2) {
      const err = validateStep2();
      if (err) { setError(err); showToast(err, 'error'); return; }
      setStep(3);
    }
  };

  const handleGetLocation = () => {
    if (!navigator?.geolocation) {
      setError('Geolocation not supported by this browser.');
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        set('latitude', parseFloat(pos.coords.latitude.toFixed(6)));
        set('longitude', parseFloat(pos.coords.longitude.toFixed(6)));
        setGpsLoading(false);
        showToast('Location detected', 'success');
      },
      () => {
        setError('Could not get location. Drop a pin on the map instead.');
        setGpsLoading(false);
      },
      { timeout: 10000 },
    );
  };

  const handleMapChange = (lat: number, lng: number) => {
    set('latitude', lat);
    set('longitude', lng);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.latitude == null || form.longitude == null || !disclaimerAccepted) return;
    setError('');
    try {
      const result = await createRequest({
        service_type: form.service_type,
        description: form.description.trim(),
        address: form.address.trim(),
        latitude: form.latitude,
        longitude: form.longitude,
        deadline: form.deadline || undefined,
        preferred_date: form.preferred_date || undefined,
      });
      showToast('Request submitted successfully!', 'success');
      navigate(`/client/request/${result.id}`);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to submit request';
      setError(msg);
      showToast(msg, 'error');
    }
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.pageHeader}>
        <button onClick={() => navigate(-1)} style={styles.backBtn} className="sc-press">
          ← Dashboard
        </button>
        <h1 style={styles.title}>New Survey Request</h1>
        <p style={styles.subtitle}>Submit a request to connect with certified surveyors.</p>
      </div>

      {/* Step Indicator */}
      <div style={styles.stepIndicatorRow}>
        {[1, 2, 3].map((s) => (
          <React.Fragment key={s}>
            <div style={{
              ...styles.stepDot,
              background: s < step ? 'var(--sc-success)' : s === step ? 'var(--sc-brand-gradient)' : 'var(--sc-bg-input)',
              color: s <= step ? '#FFF' : 'var(--sc-text-tertiary)',
              border: s <= step ? 'none' : '1px solid var(--sc-border)',
            }}>
              {s < step ? '✓' : s}
            </div>
            {s < 3 && <div style={{
              ...styles.stepLine,
              background: s < step ? 'var(--sc-success)' : 'var(--sc-border)',
            }} />}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div style={styles.errorBox} className="sc-animate-toast-in">
          <AnimatedIcon name="warning" size={16} color="var(--sc-error)" />
          <span style={{ fontSize: 13, fontWeight: 500, marginLeft: 8 }}>{error}</span>
        </div>
      )}

      {/* Main card */}
      <div style={styles.card} className="sc-animate-scale-in">
        {/* Step 1: Form Details */}
        {step === 1 && (
          <div style={styles.formSection}>
            <h3 style={styles.sectionTitle}>Request Details</h3>
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>Service Type *</label>
              <select
                value={form.service_type}
                onChange={(e) => set('service_type', e.target.value)}
                style={styles.select}
                required
              >
                <option value="">Select a service type...</option>
                {SURVEY_SERVICE_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Description * (min 10 characters)</label>
              <textarea
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="Specify survey boundaries, access instructions, or special requests..."
                rows={5}
                style={styles.textarea}
                required
                minLength={10}
              />
              <div style={styles.charCount}>{form.description.length} characters</div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Property Address *</label>
              <input
                value={form.address}
                onChange={(e) => set('address', e.target.value)}
                placeholder="e.g. 100 Leopold Takawira St, Harare"
                style={styles.input}
                required
              />
            </div>

            <div style={styles.row}>
              <div style={styles.col}>
                <label style={styles.label}>Deadline (optional)</label>
                <input
                  type="date"
                  value={form.deadline}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => set('deadline', e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.col}>
                <label style={styles.label}>Preferred Date (optional)</label>
                <input
                  type="date"
                  value={form.preferred_date}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => set('preferred_date', e.target.value)}
                  style={styles.input}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Location Map */}
        {step === 2 && (
          <div style={styles.formSection}>
            <h3 style={styles.sectionTitle}>Pin Survey Location</h3>
            <p style={styles.hintText}>Tap the map to set exact coordinates, or use GPS auto-detect.</p>

            <div style={styles.coordHeader}>
              <div style={styles.coordCell}>
                <span style={styles.coordLabel}>Latitude</span>
                <span style={styles.coordVal}>{form.latitude != null ? form.latitude.toFixed(6) : '—'}</span>
              </div>
              <div style={styles.coordCell}>
                <span style={styles.coordLabel}>Longitude</span>
                <span style={styles.coordVal}>{form.longitude != null ? form.longitude.toFixed(6) : '—'}</span>
              </div>
              <button
                type="button"
                onClick={handleGetLocation}
                disabled={gpsLoading}
                style={styles.gpsBtn}
                className="sc-press"
              >
                <AnimatedIcon name="bell" size={14} color="var(--sc-success-text)" />
                <span style={{ marginLeft: 6 }}>{gpsLoading ? 'Detecting...' : 'Detect GPS'}</span>
              </button>
            </div>

            <div style={styles.mapWrapper}>
              <MapPicker
                latitude={form.latitude}
                longitude={form.longitude}
                onChange={handleMapChange}
                height={260}
              />
            </div>

            {form.latitude == null && (
              <div style={styles.warningBox}>
                <span>⚠️ Please select a location on the map to proceed.</span>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Summary & Disclaimer */}
        {step === 3 && (
          <div style={styles.formSection}>
            <h3 style={styles.sectionTitle}>Verify & Submit</h3>
            
            <div style={styles.summaryList}>
              <div style={styles.summaryItem}>
                <span style={styles.summaryLabel}>Type</span>
                <span style={styles.summaryValue}>{form.service_type}</span>
              </div>
              <div style={styles.summaryItem}>
                <span style={styles.summaryLabel}>Address</span>
                <span style={styles.summaryValue}>{form.address}</span>
              </div>
              <div style={styles.summaryItem}>
                <span style={styles.summaryLabel}>Coordinates</span>
                <span style={styles.summaryValue}>
                  {form.latitude?.toFixed(5)}, {form.longitude?.toFixed(5)}
                </span>
              </div>
            </div>

            <div style={styles.disclaimerBox}>
              <div style={styles.disclaimerTitle}>⚠️ Platform Liability Agreement</div>
              <p style={styles.disclaimerText}>
                Survey Connect acts purely as a matching network. We do not directly employ surveyors or drillers.
                We do not accept liability for structural outcomes, report inaccuracies, or secondary drilling errors.
                Platform payment controls protect your capital until confirming milestones are cleared.
              </p>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={disclaimerAccepted}
                  onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                  style={styles.checkbox}
                />
                <span>I accept and wish to publish this project request.</span>
              </label>
            </div>
          </div>
        )}

        {/* Action Controls */}
        <div style={styles.actionRow}>
          {step > 1 && (
            <button
              type="button"
              onClick={() => setStep((s) => s - 1)}
              style={styles.btnSecondary}
              className="sc-press"
            >
              Back
            </button>
          )}
          
          {step < 3 ? (
            <button
              type="button"
              onClick={handleNext}
              style={styles.btnPrimary}
              className="sc-press"
            >
              Continue
            </button>
          ) : (
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={!disclaimerAccepted || isLoading}
              style={{
                ...styles.btnPrimary,
                opacity: disclaimerAccepted && !isLoading ? 1 : 0.6,
              }}
              className="sc-press"
            >
              {isLoading ? 'Publishing...' : 'Publish Request'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    maxWidth: 520,
    margin: '0 auto',
    padding: '0 8px 40px',
    fontFamily: 'var(--sc-font-family)',
  },
  pageHeader: { marginBottom: 24 },
  backBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--sc-brand-primary)',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    padding: 0,
    marginBottom: 8,
  },
  title: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', fontFamily: 'var(--sc-font-display)', letterSpacing: '-0.5px' },
  subtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: 0 },

  stepIndicatorRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  stepDot: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    transition: 'all var(--sc-transition-fast)',
  },
  stepLine: {
    flex: 1,
    height: 2,
    margin: '0 8px',
  },

  card: {
    background: 'var(--sc-bg-secondary)',
    borderRadius: 'var(--sc-radius-lg)',
    padding: 24,
    border: '1px solid var(--sc-border)',
    boxShadow: 'var(--sc-shadow-md)',
  },
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: 'var(--sc-text-primary)',
    margin: 0,
    fontFamily: 'var(--sc-font-display)',
  },
  hintText: {
    fontSize: 13,
    color: 'var(--sc-text-secondary)',
    margin: 0,
  },

  errorBox: {
    display: 'flex',
    alignItems: 'center',
    background: 'var(--sc-error-bg)',
    border: '1px solid var(--sc-error)',
    borderRadius: 'var(--sc-radius-md)',
    padding: '12px 16px',
    color: 'var(--sc-error-text)',
    marginBottom: 16,
  },
  warningBox: {
    background: 'var(--sc-warning-bg)',
    border: '1px solid var(--sc-warning)',
    borderRadius: 'var(--sc-radius-md)',
    padding: '10px 14px',
    fontSize: 12.5,
    color: 'var(--sc-warning-text)',
  },

  inputGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 600, color: 'var(--sc-text-secondary)' },
  input: {
    width: '100%',
    height: 'var(--sc-touch-input)',
    padding: '0 14px',
    fontSize: 14,
    color: 'var(--sc-text-primary)',
    background: 'var(--sc-bg-input)',
    border: '1px solid var(--sc-border)',
    borderRadius: 'var(--sc-radius-md)',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all var(--sc-transition-fast)',
  },
  select: {
    width: '100%',
    height: 'var(--sc-touch-input)',
    padding: '0 14px',
    fontSize: 14,
    color: 'var(--sc-text-primary)',
    background: 'var(--sc-bg-input)',
    border: '1px solid var(--sc-border)',
    borderRadius: 'var(--sc-radius-md)',
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    fontSize: 14,
    color: 'var(--sc-text-primary)',
    background: 'var(--sc-bg-input)',
    border: '1px solid var(--sc-border)',
    borderRadius: 'var(--sc-radius-md)',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  charCount: { fontSize: 11, color: 'var(--sc-text-tertiary)', textAlign: 'right' },
  row: { display: 'flex', gap: 12 },
  col: { flex: 1, display: 'flex', flexDirection: 'column', gap: 6 },

  coordHeader: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    background: 'var(--sc-bg-input)',
    padding: 10,
    borderRadius: 'var(--sc-radius-md)',
    border: '1px solid var(--sc-border)',
  },
  coordCell: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  coordLabel: { fontSize: 9, fontWeight: 700, color: 'var(--sc-text-tertiary)', textTransform: 'uppercase' },
  coordVal: { fontSize: 13, fontWeight: 700, color: 'var(--sc-text-primary)', marginTop: 2 },
  gpsBtn: {
    height: 38,
    padding: '0 14px',
    background: 'var(--sc-success-bg)',
    border: '1px solid var(--sc-success)',
    borderRadius: 'var(--sc-radius-sm)',
    color: 'var(--sc-success-text)',
    fontSize: 12.5,
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mapWrapper: {
    borderRadius: 'var(--sc-radius-lg)',
    overflow: 'hidden',
    border: '1px solid var(--sc-border)',
  },

  summaryList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    background: 'var(--sc-bg-input)',
    padding: 14,
    borderRadius: 'var(--sc-radius-md)',
    border: '1px solid var(--sc-border)',
  },
  summaryItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid var(--sc-border-light)',
  },
  summaryLabel: { fontSize: 13, color: 'var(--sc-text-secondary)' },
  summaryValue: { fontSize: 13, fontWeight: 700, color: 'var(--sc-text-primary)' },

  disclaimerBox: {
    background: 'var(--sc-warning-bg)',
    border: '1px solid var(--sc-warning)',
    borderRadius: 'var(--sc-radius-md)',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  disclaimerTitle: { fontSize: 13, fontWeight: 700, color: 'var(--sc-warning-text)' },
  disclaimerText: { fontSize: 12, color: 'var(--sc-text-secondary)', lineHeight: 1.5, margin: 0 },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    fontSize: 12.5,
    fontWeight: 600,
    color: 'var(--sc-warning-text)',
    cursor: 'pointer',
  },
  checkbox: {
    marginTop: 2,
    accentColor: 'var(--sc-brand-primary)',
    cursor: 'pointer',
  },

  actionRow: {
    display: 'flex',
    gap: 12,
    marginTop: 24,
    justifyContent: 'flex-end',
  },
  btnSecondary: {
    height: 'var(--sc-touch-btn)',
    padding: '0 24px',
    background: 'transparent',
    border: '1px solid var(--sc-border)',
    borderRadius: 'var(--sc-radius-md)',
    color: 'var(--sc-text-secondary)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnPrimary: {
    height: 'var(--sc-touch-btn)',
    padding: '0 28px',
    background: 'var(--sc-brand-gradient)',
    color: 'var(--sc-text-on-brand)',
    border: 'none',
    borderRadius: 'var(--sc-radius-md)',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxShadow: 'var(--sc-shadow-brand)',
  },
};
